// Google Analytics 4, production only.
//
// The gate is the hostname rather than a build flag. Both stages run the same
// `npm run build`, and prod is also deployable by hand
// (`npm run cdk:deploy -- -c stage=prod`), so a build-time env var would either
// have to be threaded through every path or would silently ship prod without
// analytics the one time someone forgot it. Keying on the host means any bundle
// behaves correctly wherever it runs: the dev distribution, a local preview and
// localhost all stay silent, and only the real domain reports.
//
// The measurement ID is not a secret — GA4 exposes it in the page source of
// every site using it — so committing it costs nothing.

const GA_MEASUREMENT_ID = 'G-XQHQTDPCZQ';

/** Only this host reports. Keep in sync with domainName in infra/config.ts. */
const ANALYTICS_HOST = 'ratemyhysa.com';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export function isAnalyticsEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === ANALYTICS_HOST;
}

let initialized = false;

/**
 * Inject gtag.js once. `send_page_view` is disabled because GA4 would otherwise
 * count only the first document load; with client-side routing every subsequent
 * navigation has to be reported explicitly by trackPageView.
 */
export function initAnalytics(): void {
  if (initialized || !isAnalyticsEnabled()) return;
  initialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // gtag must forward `arguments` verbatim — GA reads it as an Arguments
  // object, so a rest-parameter array here would be recorded incorrectly.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
}

/** Report one page view. Path should include the query string, if any. */
export function trackPageView(path: string): void {
  if (!isAnalyticsEnabled() || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}
